import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { Cadence, DaysOfTheWeek } from '../types';
import { checkIfDurationIsATypicalOngoingOffer } from '../utils';
import CadenceInput from './CadenceInput';
import { ScheduleEndDateWarning } from './ScheduleEndDateWarning';
import StartOrEndDateInput from './StartOrEndDateInput';
export interface Schedule {
  startDate?: string;
  endDate?: string;
  cadence: Cadence;
}

export interface SchedulingInputProps {
  takerMarkets: TakerMarket[];
  schedule: Schedule;
  setStartDate: (date: string | undefined) => void;
  setEndDate: (date: string | undefined) => void;
  setCadence: (cadence: Cadence) => void;
  errors: {
    cadence?: {
      message: string;
    };
  };
}

export const SchedulingInput = ({
  takerMarkets,
  schedule,
  setStartDate,
  setEndDate,
  setCadence,
  errors,
}: SchedulingInputProps) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();
  const { startDate, endDate, cadence } = schedule;
  const isDefaultDuration = checkIfDurationIsATypicalOngoingOffer({
    startDate,
    endDate,
    cadence,
  });

  const handleRestoreOngoingSchedule = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setCadence({ type: 'daily', value: DaysOfTheWeek });
    trackEnterpriseEvent('offer::schedule::form::reset');
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col items-start justify-between gap-x-8 gap-y-3 md:flex-row md:items-center">
        <p className="text-sm">{t('scheduling.review')}</p>
        <Button variant="tertiary" size="sm" disabled={isDefaultDuration} onClick={handleRestoreOngoingSchedule}>
          {t('scheduling.restoreOngoingSchedule')}
        </Button>
      </div>
      <CadenceInput
        takerMarkets={takerMarkets}
        cadence={cadence}
        startDate={startDate}
        endDate={endDate}
        setCadence={setCadence}
        errors={errors}
      />
      <div className="space-y-1">
        <StartOrEndDateInput
          type="start"
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
        <StartOrEndDateInput
          type="end"
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </div>
      {!!endDate && (
        <QueryBoundaries>
          <ScheduleEndDateWarning takerMarkets={takerMarkets} startDate={startDate} endDate={endDate} />
        </QueryBoundaries>
      )}
    </div>
  );
};
